import { Injectable } from '@angular/core';
import { IgenomicsState } from '../../core/interfaces/genomics-state.interface';
import { Store } from '@ngrx/store';
import { HttpInterceptorService } from '../../core/services/http-interceptor.service';
import {
  IAdvancedSearchConfiguration,
  IAdvancedSearchQueryConfiguration
} from '../components/case-search/advanced-search-configuration.interface';
import { setAdvancedSearchConfiguration } from '../store/advanced-search.actions';
import { Observable } from 'rxjs/Observable';
import { isEmpty, cloneDeep } from 'lodash';
import { NavigationConfigService } from '../navigation/services/navigationConfig.service';
import { SessionStorageService } from '@ispm/shared/services/session-storage.service';
import { BrowserCacheService } from '@ispm/core/services/browser-cache.service';
import { SimpleSearchService } from '../../worklist/services/simple-search.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';

@Injectable()
export class AdvancedSearchService {

  public searchConfigLoaded: boolean = false;

  private getConfigUrl = 'workflow/advancedSearchConfig';
  private worklistConfig: IAdvancedSearchQueryConfiguration;
  private searchConfigParameters: Array<IAdvancedSearchConfiguration>;

  constructor(
    private store: Store<IgenomicsState>,
    private http: HttpInterceptorService,
    private authService: AuthenticationService,
    private navigationConfigService: NavigationConfigService,
    private browserCacheService: BrowserCacheService,
    private simpleSearchService: SimpleSearchService
  ) {
  }

  reloadWorklistParameters() {
    // Update filters when changing worklist BUT application was not changed (so do not need to get the configuration again from the server).
    // It is in use when swiching tabs but staying at the same application (e.g. in lung).
    const workListDynamicSearch = JSON.parse(sessionStorage.getItem(SessionStorageService.CURRENT_WORKLIST_DYNAMIC_SEARCH));
    if (workListDynamicSearch) {
      this.worklistConfig.dataQuery = workListDynamicSearch.dynamicSearch.dataQuery;
      this.searchConfigParameters.forEach(config => {
        const unifiedConfig: IAdvancedSearchConfiguration = this.buildUnifiedConfig(config);
        if (unifiedConfig) {
          this.store.dispatch(setAdvancedSearchConfiguration(unifiedConfig));
        }
      });
    }
    else {
      console.error('AdvancedSearchService->reloadWorklistParameters: unable to load worklist parameters');
    }
  }

  refetch(): void {
    this.worklistConfig = undefined;
    this.searchConfigLoaded = false;
    this.initAdvancedSearch();
  }

  private async fillConfigFromWorklistDynamicSearch(): Promise<void>  {
    this.worklistConfig = { } as IAdvancedSearchQueryConfiguration;

    const timeout: number = 60000; // 60 seconds
    const startTime: number = Date.now();

    // Waiting for worklist query config to be loaded to the seasion storage - worklist query config is loaded asynchronously
    while (isEmpty(sessionStorage.getItem(SessionStorageService.CURRENT_WORKLIST_DYNAMIC_SEARCH)) && Date.now() - startTime < timeout) {
      await new Promise(resolve => setTimeout(resolve, 250));
    }
    const workListDynamicSearch = JSON.parse(sessionStorage.getItem(SessionStorageService.CURRENT_WORKLIST_DYNAMIC_SEARCH));

    if (!isEmpty(workListDynamicSearch)) {
      this.worklistConfig.dataQuery = workListDynamicSearch.dynamicSearch.dataQuery;
      return Promise.resolve();
    }
    else {
      return Promise.reject();
    }
  }

  private initAdvancedSearch(): void {
    this.searchConfigParameters = [];
    const appId: string = this.navigationConfigService.getCurrentApplicationId();
    this.http.get<IAdvancedSearchConfiguration>(this.getConfigUrl, { 'app_id': appId, cacheId: this.browserCacheService.getCacheId()}, true)
      .map(response => response.body)
      .catch(err => {
        console.error('Error fetching advanced search configurations for appId: ' + appId, err);
        return Observable.of(null);
      })
      .take(1).subscribe(config => {
        if (!isEmpty(config)) {
          this.searchConfigParameters.push(config);
          if (!this.worklistConfig) {
            this.fillConfigFromWorklistDynamicSearch().then(
              () => { // Success
                const unifiedConfig: IAdvancedSearchConfiguration = this.buildUnifiedConfig(config);
                if (unifiedConfig) {
                  this.store.dispatch(setAdvancedSearchConfiguration(unifiedConfig));
                }
                else {
                  // That means that queryConfig is missing. It should not happen.
                  console.error('AdvancedSearchService->initAdvancedSearch unifiedConfig has failed because queryConfig is missing');
                }
                this.searchConfigLoaded = true;
              },
              () => { // Config was not loaded
                console.error('AdvancedSearchService -> initAdvancedSearch: worklist condiguration was not found for: ' + appId + '. Search Patient is disabled');
              }
            );
          }
        }
        else {
          // This should not happen and 'config' should not be empty because if a certain organization config was not found, default config is returned from the server
            console.error('initAdvancedSearch error: DEFAULT configuration is not defined!');
        }
      });
  }

  private buildUnifiedConfig(advancedSearchConfig: IAdvancedSearchConfiguration): IAdvancedSearchConfiguration {
    // Getting AdvancedSearchConfig from DB and adding to it configuration parameters from the current worklist
    let searchConfigParameters: IAdvancedSearchConfiguration = cloneDeep(advancedSearchConfig);
    if (searchConfigParameters.queryConfig) {
      searchConfigParameters.queryConfig.forEach(config => {
          const isAdmin = (this.authService.user.role === 'GenomicsAdminGroup');
          if (this.simpleSearchService.isSimpleSearch(config)) {
            if (isAdmin) { // if current user is admin - remove 'active' filter
              delete config.simpleSearchQuery.active;
            }
          }
          else {
        // Taking worklist sort definition only if sort was not defined in advancedSearchConfig
        if (!config.dataQuery.sort) {
          config.dataQuery.sort = this.worklistConfig.dataQuery.sort;
        }
        config.dataQuery.relations = config.dataQuery.relations.concat(this.worklistConfig.dataQuery.relations);

        // Adding worklist filters to advancedSearchConfig filters.
        // If same filter (by path) exists in advancedSearchConfig and in the worklist, the one from advancedSearchConfig will be used.
        if (this.worklistConfig.dataQuery.filters) {
          for (const filterConfig of this.worklistConfig.dataQuery.filters) {
            if (config.dataQuery.filters.findIndex(filter => filter.path === filterConfig.path) === -1) {
              config.dataQuery.filters.push(filterConfig);
            }
          }
        }
        // if current user is admin - remove 'active' filter
        if (isAdmin) {
          config.dataQuery.filters = config.dataQuery.filters.filter(filter => filter.path !== 'active');
        }

        // Remove archived filter from the filters list that are taken from the worklist
        // because worklist should not present archived tests but Search Patint should find them.
        // Using includes() because the the filter path can be in curly braces.
        config.dataQuery.filters = config.dataQuery.filters.filter(filter => !filter.path.toLowerCase().includes('activeworkflow')
          && !filter.path.toLowerCase().includes('wlrdoactivewfl'));
          }
      });
    }
    else {
      console.error('AdvancedSearchService->buildUnifiedConfig: cannot find queryConfig in advancedSearchConfig');
      searchConfigParameters = null;
    }
    return searchConfigParameters;
  }
}
